import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  type ElementRef,
  inject,
  viewChild,
} from '@angular/core';
import {
  LucideAstroid,
  LucideEllipsisVertical,
  LucideMenu,
  LucidePlus,
  LucideTrash2,
  LucideX,
} from '@lucide/angular';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  H3Component,
  PmButtonComponent,
  PmButtonMenuDirective,
  PmIconComponent,
  PmLinkComponent,
  type PmMenuItem,
} from '@libs/ui';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { selectLoggedInUser } from '@core/store/auth/auth.selectors';
import { ChatStatus } from '../../models/chat';
import type { Suggestion, ToolCallLine } from '../../models/chat';
import { ChatStateService } from '../../services/chat-state.service';
import { ChatHistoryService } from '../../services/chat-history.service';
import { ChatStatusComponent } from '../chat-status/chat-status';
import { ChatComposerComponent } from '../chat-composer/chat-composer';
import { ChatHistoryComponent } from '../chat-history/chat-history';
import { ChatMessageComponent } from '../chat-message/chat-message';
import { ChatSuggestionsComponent } from '../chat-suggestions/chat-suggestions';
import { ChatToolCallsComponent } from '../chat-tool-calls/chat-tool-calls';
import type { Message } from '@ag-ui/client';

const STARTER_SUGGESTION_KEYS = [
  { id: 'starter-1', key: 'chat.suggestions.starter1' },
  { id: 'starter-2', key: 'chat.suggestions.starter2' },
  { id: 'starter-3', key: 'chat.suggestions.starter3' },
];

const STARTER_KEYS = STARTER_SUGGESTION_KEYS.map(({ key }) => key);

const MENU_PANEL_CLASS = [
  '[--mat-menu-item-label-text-size:14px]',
  '[--mat-menu-item-label-text-line-height:16px]',
  '[--mat-menu-item-label-text-tracking:0]',
  '[--mat-menu-item-leading-spacing:12px]',
  '[--mat-menu-item-trailing-spacing:12px]',
  '[&.mat-mdc-menu-panel]:min-w-[212px]',
  '[&_.mat-mdc-menu-content]:px-1',
  '[&_.mat-mdc-menu-item]:mt-0',
  '[&_.mat-mdc-menu-item]:min-h-[38px]',
  '[&_.mat-mdc-menu-item_span]:gap-2',
  '[&_.mat-mdc-menu-item_span]:pr-0',
].join(' ');

@Component({
  selector: 'pm-chat-rail',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-rail.html',
  host: { class: 'flex h-full min-h-0 flex-1 flex-col' },
  imports: [
    TranslatePipe,
    H3Component,
    PmButtonComponent,
    PmButtonMenuDirective,
    PmIconComponent,
    PmLinkComponent,
    ChatComposerComponent,
    ChatHistoryComponent,
    ChatMessageComponent,
    ChatStatusComponent,
    ChatSuggestionsComponent,
    ChatToolCallsComponent,
  ],
})
export class ChatRailComponent {
  protected readonly chat = inject(ChatStateService);
  protected readonly chatHistory = inject(ChatHistoryService);
  private readonly translateService = inject(TranslateService);
  private readonly store = inject(Store);

  protected readonly LucideMenu = LucideMenu;
  protected readonly LucideEllipsisVertical = LucideEllipsisVertical;
  protected readonly LucideAstroid = LucideAstroid;
  protected readonly LucideX = LucideX;

  protected readonly menuPanelClass = MENU_PANEL_CLASS;

  protected readonly menuConfig = computed<PmMenuItem[]>(() => {
    const disabled = !this.chat.hasConversation();
    return [
      {
        icon: LucidePlus,
        text: 'chat.menu.startNewChat',
        disabled,
        handleClick: () => this.chat.startNewChat(),
      },
      {
        icon: LucideTrash2,
        text: 'chat.menu.deleteChat',
        disabled,
        handleClick: () => this.chat.deleteSession(this.chat.sessionId),
      },
    ];
  });

  private readonly starterTranslations = toSignal(
    this.translateService.stream(STARTER_KEYS) as Observable<
      Record<string, string>
    >,
    { initialValue: {} },
  );

  protected readonly starterSuggestions = computed<Suggestion[]>(() => {
    const translations = this.starterTranslations() as Record<string, string>;
    return STARTER_SUGGESTION_KEYS.map(({ id, key }) => ({
      id,
      text: translations[key] ?? key,
    }));
  });

  private readonly user = this.store.selectSignal(selectLoggedInUser);
  protected readonly firstName = computed(() => this.user()?.firstName ?? '');

  protected readonly showStatus = computed(() => {
    const status = this.chat.status();
    return (
      status === ChatStatus.Thinking ||
      status === ChatStatus.Searching ||
      status === ChatStatus.Working
    );
  });

  private readonly content = viewChild<ElementRef<HTMLDivElement>>('content');

  constructor() {
    effect(() => {
      const messages = this.chat.messages();
      const status = this.chat.status();
      const element = this.content()?.nativeElement;
      if (!element || (status === ChatStatus.Start && messages.length === 0)) {
        return;
      }
      queueMicrotask(() => {
        element.scrollTop = element.scrollHeight;
      });
    });
  }

  protected onSuggestion(text: string): void {
    this.chat.submit(text);
  }

  protected routeToSupport(): void {
    window.open(environment.supportUrl, '_blank', 'noopener');
  }

  protected textOf(msg: Message): string {
    return typeof msg.content === 'string' ? msg.content : '';
  }

  protected readonly toolCallGroups = computed(() =>
    this.groupToolCallsByMessage(),
  );

  private groupToolCallsByMessage(): Map<string, ToolCallLine[]> {
    const messages = this.chat.messages();
    const lastAssistantId = messages.reduce<string | undefined>(
      (id, msg) => (msg.role === 'assistant' ? msg.id : id),
      undefined,
    );
    const groups = new Map<string, ToolCallLine[]>();
    for (const call of this.chat.toolCalls()) {
      const key = call.parentMessageId ?? lastAssistantId;
      if (!key) {
        continue;
      }
      const list = groups.get(key) ?? [];
      list.push(call);
      groups.set(key, list);
    }
    return groups;
  }

  protected toolCallsFor(messageId: string): ToolCallLine[] {
    return this.toolCallGroups().get(messageId) ?? [];
  }
}
